/**
 * @file CoreTelemetry - Platform-agnostic telemetry types for logs, metrics,
 * and distributed traces.
 *
 * These types define the contracts for observability across LabCraft.
 * They are intentionally platform-agnostic and can be used in:
 * - Node.js servers
 * - Browsers (React, vanilla JS)
 * - Edge workers
 *
 * Platform-specific implementations (e.g., AsyncLocalStorage for span context
 * propagation in Node.js) live in `@labcraft/core/backend`.
 *
 * @example
 * ```typescript
 * import {
 *   LOG_LEVEL_VALUES,
 *   type LogLevel,
 *   type MetricLabels,
 *   type Span,
 * } from '@labcraft/core'
 *
 * // Check log level
 * if (LOG_LEVEL_VALUES[level] >= LOG_LEVEL_VALUES.warn) {
 *   // This is a warning or error
 * }
 *
 * // Type-safe metric labels
 * const labels: MetricLabels = {
 *   endpoint: '/api/users',
 *   status: 200,
 * }
 * ```
 *
 * @module
 */

import { CoreError } from './core_error.ts'

////////////////////////////////////////////////////////////////////////////////
// Log Types
////////////////////////////////////////////////////////////////////////////////

/**
 * Log level names in order of increasing severity.
 *
 * - `debug`: Detailed information for debugging (noisy, disabled in production)
 * - `info`: Normal operational messages (requests, responses, state changes)
 * - `warn`: Unexpected but recoverable situations (retries, deprecations)
 * - `error`: Failures requiring attention (unhandled errors, outages)
 */
export const LOG_LEVEL_NAMES = ['debug', 'info', 'warn', 'error'] as const

/**
 * Log level type derived from LOG_LEVEL_NAMES.
 */
export type LogLevel = (typeof LOG_LEVEL_NAMES)[number]

/**
 * Numeric values for log levels, enabling fast comparison.
 *
 * @example
 * ```typescript
 * // Check if a level should be logged
 * if (LOG_LEVEL_VALUES[level] >= LOG_LEVEL_VALUES[threshold]) {
 *   emit(level, message)
 * }
 * ```
 */
export const LOG_LEVEL_VALUES: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
} as const

/**
 * Structured log entry for serialization and transport.
 *
 * **Schema stability contract** — see
 * [ADR-021](../../../../../docs/architecture/021-log-schema-stability.md).
 *
 * `LogEntry`'s field set is the wire schema every operator dashboard, LogQL
 * query, alert rule, and downstream consumer (Loki, Tempo correlation, the
 * `EphemeralDaemon` subprocess-stdout parser via {@link isLogEntry}) pins
 * against. Treat it as an API surface:
 *
 * - **Additions are additive.** Adding a new optional field never breaks a
 *   `| json` consumer.
 * - **Renames and removals are breaking.** Renaming `event` → `eventName` or
 *   dropping `source` invalidates every dashboard tied to that key.
 *   Coordinate downstream changes when this happens.
 * - **Levels are a fixed enum.** {@link LogLevel} is `'debug' | 'info' |
 *   'warn' | 'error'`. Adding a new level is a downstream-visible change
 *   (alert routing, level-floor sinks) — treat it as a renaming-equivalent
 *   breaking change.
 * - **Per-event `context` keys are also a stable contract.** The same
 *   `event` value should produce the same `context` keys every emit; adding a
 *   key is additive, renaming or sometimes-omitting breaks `| json | <key>=…`
 *   queries. The keys are curated at the call site (#317's Phase 3) and via
 *   each `CoreClass` subclass's `toJSON()` (#317's Phase 2). `CoreClass`'s log
 *   methods coerce a `CoreClass`-instance `context` arg through `toJSON()` so
 *   raw private fields (`_state`, `_logLevel`, `events`) can't leak into the
 *   schema — the defence-in-depth backstop landed in #317 Phase 1.
 *
 * @property timestamp - ISO 8601 timestamp when the log was created.
 * @property level - Severity level (`debug` / `info` / `warn` / `error`).
 * @property event - Dot-namespaced event identifier (e.g.,
 *   `'api.request.handled'`, `'db.query.slow'`). The grep target for
 *   `| json | event=~"..."` queries; keep stable.
 * @property source - Full hierarchical ID of the originating component
 *   (e.g., `'daemon.http.conn-1'`). Traces a log line back to the exact
 *   node in the service tree.
 * @property traceId - Trace ID for distributed-tracing correlation (when
 *   available). Pair with Tempo for click-through pivots.
 * @property spanId - Span ID within the trace (when available).
 * @property hierarchy - Snapshot of each ancestor component's `toJSON()`
 *   state at logging time, keyed by component ID. Snapshot is taken at
 *   entry-build time inside the sink (after the event has bubbled all
 *   the way up), not at the moment the originating child first emitted —
 *   the two times almost always coincide because the bubble walk is
 *   synchronous, but a reader debugging an entry whose source is several
 *   hops away should treat hierarchy values as "right-now" snapshots of
 *   each ancestor, not frozen captures from emit-time.
 * @property context - Optional per-event payload. `CoreClass` instances
 *   passed here are routed through their `toJSON()` so the keys reflect
 *   the public surface, not raw private fields. Plain objects (call-site
 *   deltas) pass through unchanged.
 */
export type LogEntry = {
  timestamp: string
  level: LogLevel
  event: string
  source: string
  traceId?: string
  spanId?: string
  hierarchy: Record<string, unknown>
  context?: Record<string, unknown>
}

////////////////////////////////////////////////////////////////////////////////
// Metric Types
////////////////////////////////////////////////////////////////////////////////

/**
 * Supported metric types following Prometheus conventions.
 *
 * - `counter`: Monotonically increasing value (requests, bytes, errors)
 * - `gauge`: Point-in-time value that goes up or down (connections, queue-size)
 * - `histogram`: Distribution of values (latencies, sizes)
 */
export const METRIC_TYPES = ['counter', 'gauge', 'histogram'] as const

/**
 * Metric type derived from METRIC_TYPES.
 */
export type MetricType = (typeof METRIC_TYPES)[number]

////////////////////////////////////////////////////////////////////////////////
// Typed metric / label name brands
//
// Prometheus requires metric and label names match `[a-z_][a-z0-9_]*` per
// segment (we additionally allow dot-separated namespaces because that's how
// CoreClass bubbles names through the hierarchy; `MetricsSink.safeName()`
// folds dots to underscores at exposition time). Names containing dashes,
// uppercase, or other characters produce spec-invalid Prometheus output the
// moment they reach `/telemetry/metrics`. The brand catches that at compile
// time for literals and at runtime via the `parse*` validators for dynamic
// strings — so a typo like `'api-requests'` is a type error, not a 2am
// pager.
////////////////////////////////////////////////////////////////////////////////

declare const __metricNameBrand: unique symbol
declare const __labelNameBrand: unique symbol

/**
 * Branded metric name. Carry one of these instead of a raw `string` to
 * indicate the value has been validated against Prometheus' name rules.
 *
 * Construct from a literal via {@link metricName} (type-checked at compile
 * time, zero runtime cost) or from a dynamic string via
 * {@link parseMetricName} (validated at runtime, throws `CoreError({ code:
 * 'INVALID_METRIC_NAME' })` on bad input).
 */
export type MetricName = string & { readonly [__metricNameBrand]: true }

/**
 * Branded label name. Same shape as {@link MetricName}; constructed via
 * {@link labelName} (literal) or {@link parseLabelName} (runtime).
 */
export type LabelName = string & { readonly [__labelNameBrand]: true }

/**
 * Single-segment validation: lowercase letter or underscore, followed by any
 * number of lowercase letters, digits, or underscores.
 */
// prettier-ignore
type Lower =
  | 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h' | 'i' | 'j' | 'k' | 'l'
  | 'm' | 'n' | 'o' | 'p' | 'q' | 'r' | 's' | 't' | 'u' | 'v' | 'w' | 'x'
  | 'y' | 'z' | '_'
// prettier-ignore
type Digit = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9'
type LowerOrDigit = Lower | Digit

/**
 * True iff every character of `S` is a lowercase letter, digit, or
 * underscore (i.e. matches the body of a Prometheus identifier segment).
 */
type AllLowerOrDigit<S extends string> = S extends ''
  ? true
  : S extends `${LowerOrDigit}${infer Rest}`
    ? AllLowerOrDigit<Rest>
    : false

/**
 * True iff `S` is a single Prometheus-valid identifier segment (`[a-z_][a-z0-9_]*`).
 */
type ValidSegment<S extends string> = S extends `${Lower}${infer Rest}`
  ? AllLowerOrDigit<Rest>
  : false

/**
 * True iff `S` is a dot-separated sequence of Prometheus-valid identifier
 * segments (`[a-z_][a-z0-9_]*(\\.[a-z_][a-z0-9_]*)*`). Recursive helper for
 * {@link ValidName}.
 */
type IsValidName<S extends string> = S extends `${infer Head}.${infer Tail}`
  ? ValidSegment<Head> extends true
    ? IsValidName<Tail>
    : false
  : ValidSegment<S>

/**
 * Template-literal validator for dot-separated Prometheus-valid names.
 * Yields the same string type `S` on success, `never` on failure — so a
 * function parameter typed `ValidName<S>` rejects invalid literals at the
 * call site.
 */
export type ValidName<S extends string> =
  IsValidName<S> extends true ? S : never

const NAME_REGEX = /^[a-z_][a-z0-9_]*(\.[a-z_][a-z0-9_]*)*$/

/**
 * Lift a string literal into a {@link MetricName} at compile time. The
 * argument's type is constrained to a Prometheus-valid form via
 * {@link ValidName}, so `metricName('api-requests')` is a type error.
 *
 * @param s - Literal metric name matching `[a-z_][a-z0-9_]*(\.[a-z_][a-z0-9_]*)*`
 * @returns The same string, branded as a {@link MetricName}.
 */
export function metricName<S extends string>(s: ValidName<S>): MetricName {
  return s as unknown as MetricName
}

/**
 * Tuple variant of {@link metricName} for declaring several names in one
 * statement (e.g. at module top level).
 */
export function metricNames<const T extends readonly string[]>(
  ...names: { [K in keyof T]: ValidName<T[K]> }
): { [K in keyof T]: MetricName } {
  return names as unknown as { [K in keyof T]: MetricName }
}

/**
 * Lift a string literal into a {@link LabelName} at compile time. Same
 * validation as {@link metricName}.
 */
export function labelName<S extends string>(s: ValidName<S>): LabelName {
  return s as unknown as LabelName
}

/**
 * Tuple variant of {@link labelName}.
 */
export function labelNames<const T extends readonly string[]>(
  ...names: { [K in keyof T]: ValidName<T[K]> }
): { [K in keyof T]: LabelName } {
  return names as unknown as { [K in keyof T]: LabelName }
}

/**
 * Validate a dynamically-built name at runtime. Returns the input branded
 * as a {@link MetricName} on success, throws `CoreError({ code:
 * 'INVALID_METRIC_NAME' })` on failure. Use this when the name comes from a
 * non-literal source (`${operation}.duration`, config, user input).
 */
export function parseMetricName(s: string): MetricName {
  if (!NAME_REGEX.test(s)) {
    throwInvalidName('INVALID_METRIC_NAME', 'metric', s)
  }
  return s as unknown as MetricName
}

/**
 * Runtime variant of {@link labelName}.
 */
export function parseLabelName(s: string): LabelName {
  if (!NAME_REGEX.test(s)) {
    throwInvalidName('INVALID_LABEL_NAME', 'label', s)
  }
  return s as unknown as LabelName
}

function throwInvalidName(
  code: 'INVALID_METRIC_NAME' | 'INVALID_LABEL_NAME',
  kind: 'metric' | 'label',
  value: string,
): never {
  throw new CoreError({
    code,
    message: `Invalid ${kind} name "${value}" — expected /[a-z_][a-z0-9_]*(\\.[a-z_][a-z0-9_]*)*/`,
    metadata: { value },
  })
}

////////////////////////////////////////////////////////////////////////////////
// Typed event-name brands
//
// Event names emitted via `this.{debug,info,warn,error}('<event>', …)` must
// be camelCase JS-identifier-shaped per segment, dot-separated for sub-events
// (`withRetry.backoff`). This makes the bubbled `event` field a literal
// method-addressable path (see services.md § "Event-name navigation"): a
// future REPL takes `hypervisor0.firecracker.releaseNetwork` and
// walks the live class hierarchy + reflects on `releaseNetwork`. The brand
// pairs a compile-time validator (`ValidEvent<S>`) with a runtime parser
// (`parseEventName`) mirroring the metric-name pair above.
////////////////////////////////////////////////////////////////////////////////

declare const __eventNameBrand: unique symbol

/**
 * Branded event name. Carry one of these instead of a raw `string` to
 * indicate the value has been validated against the camelCase + dot-segments
 * shape. Construct from a literal via {@link eventName} or implicitly via the
 * `CoreClass.debug/info/warn/error` signatures (which accept `ValidEvent<E>`
 * for literals); from a dynamic string via {@link parseEventName} (runtime
 * validated, throws `INVALID_EVENT_NAME`).
 */
export type EventName = string & { readonly [__eventNameBrand]: true }

// prettier-ignore
type Upper =
  | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K' | 'L'
  | 'M' | 'N' | 'O' | 'P' | 'Q' | 'R' | 'S' | 'T' | 'U' | 'V' | 'W' | 'X'
  | 'Y' | 'Z'
type LowerCamel = Exclude<Lower, '_'>
type IdentBody = LowerCamel | Upper | Digit

/**
 * True iff every character of `S` is a JS-identifier body character (lower /
 * upper / digit). Tail of {@link ValidEventSegment}; underscores are
 * intentionally rejected — event names follow JS method-name conventions.
 */
type AllIdentBody<S extends string> = S extends ''
  ? true
  : S extends `${IdentBody}${infer Rest}`
    ? AllIdentBody<Rest>
    : false

/**
 * True iff `S` is a single camelCase identifier segment: starts with a
 * lowercase letter, then any number of lower / upper / digit characters.
 */
type ValidEventSegment<S extends string> =
  S extends `${LowerCamel}${infer Rest}` ? AllIdentBody<Rest> : false

/**
 * True iff `S` is a dot-separated sequence of camelCase identifier segments
 * (`[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)*`). Recursive helper for
 * {@link ValidEvent}.
 */
type IsValidEvent<S extends string> = S extends `${infer Head}.${infer Tail}`
  ? ValidEventSegment<Head> extends true
    ? IsValidEvent<Tail>
    : false
  : ValidEventSegment<S>

/**
 * Template-literal validator for camelCase + dot-segments event names. Yields
 * the same string type `S` on success, `never` on failure — so a function
 * parameter typed `ValidEvent<E>` rejects invalid literals at the call site.
 *
 * Examples:
 * - `'releaseNetwork'` → `'releaseNetwork'`
 * - `'withRetry.backoff'` → `'withRetry.backoff'`
 * - `'release-network'` → `never`  (kebab-case)
 * - `'release_network'` → `never`  (snake_case)
 * - `'ReleaseNetwork'` → `never`   (PascalCase)
 */
export type ValidEvent<S extends string> =
  IsValidEvent<S> extends true ? S : never

const EVENT_NAME_REGEX = /^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)*$/

/**
 * Lift a string literal into an {@link EventName} at compile time. The
 * argument is constrained to a camelCase + dot-segments shape via
 * {@link ValidEvent}, so `eventName('release-network')` is a type error.
 *
 * @param s - Literal event name matching `[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)*`
 * @returns The same string, branded as an {@link EventName}.
 */
export function eventName<S extends string>(s: ValidEvent<S>): EventName {
  return s as unknown as EventName
}

/**
 * Validate a dynamically-built event name at runtime. Returns the input
 * branded as an {@link EventName} on success, throws
 * `CoreError({ code: 'INVALID_EVENT_NAME' })` on failure. Use this when the
 * name comes from a non-literal source (`${verb}.${noun}`, config, etc.).
 */
export function parseEventName(s: string): EventName {
  if (!EVENT_NAME_REGEX.test(s)) {
    throw new CoreError({
      code: 'INVALID_EVENT_NAME',
      message: `Invalid event name "${s}" — expected /[a-z][a-zA-Z0-9]*(\\.[a-z][a-zA-Z0-9]*)*/`,
      metadata: { value: s },
    })
  }
  return s as unknown as EventName
}

/**
 * Labels for metrics, used for filtering and grouping in dashboards.
 *
 * Labels are key-value pairs that identify the specific instance or
 * dimension of a metric. Keep cardinality low (avoid user IDs, request IDs).
 *
 * Keys are untyped `string` at this surface so existing call sites — most
 * of which use object literals like `{ status: 200, method: 'GET' }` —
 * continue to type-check. Compile-time validation against Prometheus name
 * rules is applied per-key via {@link ValidatedLabels} on the
 * `CoreClass.counter/gauge/histogram` signatures.
 */
export type MetricLabels = Record<string, string | number>

/**
 * Per-key compile-time validation for a metric-label object. Used as a
 * conditional intersection in the metric-emit signatures: if every key of
 * `L` is a valid Prometheus identifier, the result equals `L`; if any key
 * isn't, that key's value resolves to `never` so the call site fails to
 * type-check.
 */
export type ValidatedLabels<L extends Record<string, string | number>> = {
  [K in keyof L]: K extends string
    ? ValidName<K> extends never
      ? never
      : L[K]
    : never
}

////////////////////////////////////////////////////////////////////////////////
// Span Types (Distributed Tracing)
////////////////////////////////////////////////////////////////////////////////

/**
 * Span status indicating the outcome of the traced operation.
 *
 * - `ok`: Operation completed successfully
 * - `error`: Operation failed with an error
 */
export type SpanStatus = 'ok' | 'error'

/**
 * Timestamped event within a span.
 *
 * Events mark significant points during a span's lifetime, such as
 * "cache miss", "retry attempt", or "fallback used".
 *
 * - name: Descriptive event name
 * - time: Timestamp in milliseconds (performance.now() for high resolution)
 * - attributes: Optional structured attributes for the event
 */
export type SpanEvent = {
  name: string
  time: number
  attributes?: Record<string, unknown>
}

/**
 * Minimal span context for propagation across async boundaries.
 *
 * This context identifies a span within a trace and can be propagated
 * via HTTP headers, message queues, or other transport mechanisms.
 *
 * In Node.js, context propagation is typically handled automatically
 * via AsyncLocalStorage. In browsers, it may need explicit passing.
 *
 * - traceId: Trace ID shared across all spans in a distributed trace (32 hex
 *   chars)
 * - spanId: Unique span ID within the trace (16 hex chars)
 */
export type SpanContext = {
  traceId: string
  spanId: string
}

/**
 * Span interface for distributed tracing.
 *
 * Spans represent a single operation within a trace. They track:
 * - **Identity**: spanId, parentSpanId (for nesting), traceId (for correlation)
 * - **Timing**: startTime, duration (computed on end)
 * - **Metadata**: attributes (key-value), events (timestamped), status
 *
 * Spans can be nested to form a trace tree showing the flow of a request
 * through multiple services and operations.
 *
 * Properties:
 * - spanId: Unique span ID (8 bytes = 16 hex chars)
 * - parentSpanId: Parent span ID if nested (optional)
 * - traceId: Trace ID shared across all spans (16 bytes = 32 hex chars)
 * - name: Span name (operation being traced)
 * - startTime: Start time in milliseconds (performance.now())
 *
 * Methods:
 * - setAttribute(key, value): Set an attribute on the span
 * - addEvent(name, attributes?): Add a timestamped event
 * - setStatus(status, message?): Set the span status ('ok' or 'error')
 * - end(): End the span and record duration
 *
 * @remarks
 * This interface defines the contract for span implementations. The actual
 * implementation lives in `@labcraft/core/backend` (Node.js) or can be
 * implemented per-platform.
 *
 * @example
 * ```typescript
 * // Using runInSpan helper (Node.js)
 * const result = await runInSpan('processOrder', async (span) => {
 *   span.setAttribute('orderId', orderId)
 *
 *   const items = await fetchItems(orderId)
 *   span.addEvent('itemsFetched', { count: items.length })
 *
 *   const total = await calculateTotal(items)
 *   span.setAttribute('total', total)
 *
 *   return { items, total }
 * })
 *
 * // Manual span management
 * const span = createSpan('renderComponent')
 * try {
 *   span.setAttribute('component', 'UserProfile')
 *   render()
 *   span.setStatus('ok')
 * } catch (err) {
 *   span.setStatus('error', err.message)
 *   throw err
 * } finally {
 *   span.end()
 * }
 * ```
 */
export type Span = {
  readonly spanId: string
  readonly parentSpanId?: string
  readonly traceId: string
  readonly name: string
  readonly startTime: number

  setAttribute(key: string, value: string | number | boolean): void
  addEvent(name: string, attributes?: Record<string, unknown>): void
  setStatus(status: SpanStatus, message?: string): void
  end(): void
}
